import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useCreateInstrument } from "../../api/hooks/useCreateInstrument";
import { useUpdateInstrument } from "../../api/hooks/useUpdateInstrument";
import type { components } from "../../api/schema";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

export type InstrumentResponse = components["schemas"]["InstrumentResponse"];

export const SYMBOL_MAX_LENGTH = 32;
export const DESCRIPTION_MAX_LENGTH = 255;
const SYMBOL_PATTERN = /^[A-Za-z0-9./_-]+$/;

const instrumentFormSchema = z.object({
  symbol: z
    .string()
    .trim()
    .min(1, { message: "Symbol is required" })
    .max(SYMBOL_MAX_LENGTH, {
      message: `Symbol must be ${SYMBOL_MAX_LENGTH} characters or fewer`,
    })
    .regex(SYMBOL_PATTERN, {
      message:
        "Symbol may contain only letters, digits, and the characters . / - _",
    }),
  description: z
    .string()
    .trim()
    .max(DESCRIPTION_MAX_LENGTH, {
      message: `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer`,
    }),
});

type InstrumentFormValues = z.infer<typeof instrumentFormSchema>;

export type InstrumentFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initialInstrument?: InstrumentResponse | null;
};

export function InstrumentFormDialog({
  open,
  onOpenChange,
  mode,
  initialInstrument,
}: InstrumentFormDialogProps) {
  const form = useForm<InstrumentFormValues>({
    resolver: zodResolver(instrumentFormSchema),
    defaultValues: { symbol: "", description: "" },
  });
  const createMutation = useCreateInstrument();
  const updateMutation = useUpdateInstrument();
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      form.reset(
        mode === "edit" && initialInstrument
          ? {
              symbol: initialInstrument.symbol,
              description: initialInstrument.description ?? "",
            }
          : { symbol: "", description: "" },
      );
      setSubmitError(null);
    }
  }, [open, mode, initialInstrument, form]);

  async function onSubmit(values: InstrumentFormValues) {
    setSubmitError(null);
    const description = values.description === "" ? null : values.description;
    try {
      if (mode === "create") {
        await createMutation.mutateAsync({ symbol: values.symbol, description });
      } else if (initialInstrument) {
        await updateMutation.mutateAsync({
          instrumentId: initialInstrument.instrument_id,
          body: { symbol: values.symbol, description },
        });
      }
      onOpenChange(false);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : mode === "create"
            ? "Failed to create instrument. Try again."
            : "Failed to update instrument. Try again.",
      );
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const title = mode === "create" ? "New instrument" : "Edit instrument";
  const submitLabel = mode === "create" ? "Create instrument" : "Save changes";
  const pendingLabel = mode === "create" ? "Creating…" : "Saving…";
  const description =
    mode === "create"
      ? "Add an instrument by ticker symbol. Description is optional."
      : "Update the symbol or description. Trades that reference it stay attached.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="symbol"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Symbol</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      autoComplete="off"
                      maxLength={SYMBOL_MAX_LENGTH}
                      placeholder="AAPL"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      maxLength={DESCRIPTION_MAX_LENGTH}
                      placeholder="Apple Inc."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {submitError !== null && (
              <p role="alert" className="text-sm font-medium text-error">
                {submitError}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? pendingLabel : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
